import React, { useEffect, useState } from 'react';
import { toast } from '../../../utils/toast';
import { useTranslation } from 'react-i18next';
import { DollarSign, Plus } from 'lucide-react';
import useBillingStore from '../../../store/billing';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Modal } from '../../../components/ui/Modal';
import { PageHeader } from '../../../components/ui/PageHeader';
import { EmptyState } from '../../../components/ui/EmptyState';
import { TableSkeleton } from '../../../components/ui/TableSkeleton';
import { useIsMobile } from '../../../hooks/useIsMobile';
import useDriverDirectory from '../../../hooks/useDriverDirectory';
import useOperatorCoverage from '../../../hooks/useOperatorCoverage';
import { formatCurrency, formatDate } from './utils/billingHelpers';
import ComunaZonesEditor, {
  countIncompleteZoneRows,
  zonesFromApi,
  zonesToApi,
} from './components/ComunaZonesEditor';
import PostalCodeZonesEditor, {
  countIncompletePostalRows,
  postalZonesFromApi,
  postalZonesToApi,
} from './components/PostalCodeZonesEditor';
import { useCountry } from '../../../context/CountryContext';
import { apiErrorMessage, apiErrorData, apiBodyMessages } from '../../../utils/apiErrorMessage';

const TODAY = new Date().toISOString().split('T')[0];

// El backend exige ObjectId en `driverId` (CreateDriverTariffDto.@IsMongoId); el operador
// puede pegar cualquier cosa en el campo.
const MONGO_ID_RE = /^[a-f\d]{24}$/i;

// Campos del CreateDriverTariffDto → etiqueta visible del modal.
const FIELD_LABELS = {
  driverId: 'ID del Repartidor',
  pricePerDelivery: 'Precio por entrega (neto, sin IVA)',
  pricePerFailedAttempt: 'Precio por intento fallido (opcional)',
  defaultBaseCost: 'Monto por defecto propio (opcional)',
  validFrom: 'Válido desde',
  validTo: 'Válido hasta (opcional)',
  status: 'Estado',
};

// Restricción por defecto de class-validator → español; una desconocida se deja tal cual.
const translateConstraint = (constraint, t) => {
  if (/^must be a mongodb id$/.test(constraint)) return t('no es un ID válido');
  if (/^must be a positive number$/.test(constraint)) return t('debe ser un número mayor que 0');
  if (/^must be a number conforming to the specified constraints$/.test(constraint)) return t('debe ser un número');
  const min = constraint.match(/^must not be less than (-?[\d.]+)$/);
  if (min) return t('no puede ser menor que {{min}}', { min: min[1] });
  if (/^must be a valid ISO 8601 date string$/.test(constraint)) return t('debe ser una fecha válida');
  if (/^should not be empty$/.test(constraint)) return t('es obligatorio');
  if (/^must be one of the following values/.test(constraint)) return t('tiene un valor no permitido');
  return constraint;
};

/**
 * 400 de validación → una línea por mensaje, unida con ' · '. Las zonas anidadas llegan
 * como 'zones.0.amount must not be less than 0' / 'postalCodeZones.1.postalCodes should not
 * be empty': el índice va 1-based y con el nombre visible de la fila.
 * Los mensajes con ruta no reconocida se agregan crudos al final (mejor en inglés que
 * perder una causa). Devuelve null si NINGÚN mensaje trae una ruta reconocible (ahí se usa
 * el texto del backend entero: las excepciones de negocio ya vienen en español).
 * @param {unknown} err
 * @param {(key: string, opts?: object) => string} t
 * @returns {string|null}
 */
const describeTariffError = (err, t) => {
  const lines = [];
  const unmatched = new Set();
  apiBodyMessages(apiErrorData(err)).forEach((message) => {
    const route = message.split(' ')[0];
    const zone = route.match(/^zones\.(\d+)\./);
    const postalZone = route.match(/^postalCodeZones\.(\d+)\./);
    let label = null;
    if (FIELD_LABELS[route]) label = t(FIELD_LABELS[route]);
    else if (zone) label = t('Zona {{n}}', { n: Number(zone[1]) + 1 });
    else if (postalZone) label = t('Código postal {{n}}', { n: Number(postalZone[1]) + 1 });
    if (!label) {
      unmatched.add(message);
      return;
    }
    lines.push(`${label}: ${translateConstraint(message.slice(route.length + 1), t)}`);
  });
  if (!lines.length) return null;
  return lines.concat([...unmatched]).join(' · ');
};

const EMPTY_FORM = {
  driverId: '',
  pricePerDelivery: '',
  pricePerFailedAttempt: '',
  validFrom: TODAY,
  validTo: '',
  status: 'active',
  defaultBaseCost: '',
  zones: [],
  postalZones: [],
};

// Repartidor · Precio/Entrega · Precio/Intento · Vigencia · Estado · Acciones
const GRID_COLS = '1.6fr 1fr 1.2fr 1.6fr 0.8fr auto';

const DriverTariffs = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { driverTariffs, saving, fetchDriverTariffs, createDriverTariff, updateDriverTariff } =
    useBillingStore();
  const { getName } = useDriverDirectory();
  // Tarifas por CP: mismo flag de país que la config del operador (D6: el CP aplica en
  // los dos niveles de la cascada).
  const { postalCodeRates } = useCountry();
  const postalEnabled = postalCodeRates?.enabled === true;
  // El override por repartidor se acota a la MISMA cobertura del operador: no tiene
  // sentido tarifarle a un repartidor un destino al que la empresa no llega. Esta vista
  // no recibe el doc de la empresa por prop (vive fuera de /admin/settings), así que lo
  // resuelve el hook.
  const { coverageAreas, loading: coverageLoading } = useOperatorCoverage();

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [driverFilter, setDriverFilter] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchDriverTariffs().finally(() => setLoading(false));
  }, []);

  const handleOpenCreate = () => {
    setForm(EMPTY_FORM);
    setEditId(null);
    setShowModal(true);
  };

  const handleOpenEdit = (tariff) => {
    setForm({
      driverId: tariff.driverId,
      pricePerDelivery: tariff.pricePerDelivery,
      pricePerFailedAttempt: tariff.pricePerFailedAttempt ?? '',
      validFrom: new Date(tariff.validFrom).toISOString().split('T')[0],
      validTo: tariff.validTo ? new Date(tariff.validTo).toISOString().split('T')[0] : '',
      status: tariff.status,
      defaultBaseCost: tariff.defaultBaseCost != null ? String(tariff.defaultBaseCost) : '',
      // Base efectiva del repartidor para migrar/mostrar los ajustes: su default propio o,
      // si no tiene, su pricePerDelivery (obligatorio). El default del operador no está en
      // el cliente; para este override se usa la base del repartidor.
      zones: zonesFromApi(tariff.zones, tariff.defaultBaseCost ?? tariff.pricePerDelivery),
      postalZones: postalZonesFromApi(tariff.postalCodeZones),
    });
    setEditId(tariff._id);
    setShowModal(true);
  };

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSave = async () => {
    if (!form.driverId.trim() || !form.pricePerDelivery || !form.validFrom) {
      toast.error(t('Completa los campos obligatorios'));
      return;
    }
    // Al editar el input está deshabilitado (el driverId viene del listado y ya es válido).
    if (!editId && !MONGO_ID_RE.test(form.driverId.trim())) {
      toast.error(t('El ID del repartidor debe ser el ID interno de 24 caracteres.'));
      return;
    }
    // Aviso (no bloqueante) de las reglas que el guardado descartaría por estar
    // incompletas: antes desaparecían en silencio y el operador creía haberlas guardado.
    const incomplete =
      countIncompleteZoneRows(form.zones) +
      (postalEnabled ? countIncompletePostalRows(form.postalZones) : 0);
    if (incomplete > 0) {
      toast.warn(
        t('{{count}} regla incompleta se descartará al guardar (falta el destino o el monto).', {
          count: incomplete,
        }),
      );
    }
    const payload = {
      driverId: form.driverId.trim(),
      pricePerDelivery: Number(form.pricePerDelivery),
      pricePerFailedAttempt: form.pricePerFailedAttempt ? Number(form.pricePerFailedAttempt) : undefined,
      validFrom: form.validFrom,
      validTo: form.validTo || undefined,
      status: form.status,
      // Override por repartidor (opcional): default propio + comunas propias.
      defaultBaseCost: form.defaultBaseCost !== '' ? Number(form.defaultBaseCost) : undefined,
      zones: zonesToApi(form.zones),
    };
    // `postalCodeZones` se omite cuando el país no habilita tarifas por CP, para que el
    // payload de un operador chileno sea idéntico al de antes de esta feature. Cuando sí
    // aplican se envía siempre (aunque quede vacío): así el arreglo vacío LIMPIA el
    // override, que es la semántica de `zones` en el PATCH.
    if (postalEnabled) {
      payload.postalCodeZones = postalZonesToApi(form.postalZones);
    }
    try {
      if (editId) {
        await updateDriverTariff(editId, payload);
        toast.success(t('Tarifa actualizada'));
      } else {
        await createDriverTariff(payload);
        toast.success(t('Tarifa creada. Las tarifas anteriores del repartidor fueron desactivadas automáticamente.'));
      }
      setShowModal(false);
      fetchDriverTariffs(driverFilter || undefined);
    } catch (err) {
      toast.error(describeTariffError(err, t) ?? apiErrorMessage(err, t('Error al guardar tarifa')));
    }
  };

  const filtered = driverFilter
    ? driverTariffs.filter((tr) => tr.driverId === driverFilter)
    : driverTariffs;

  const renderStatus = (status) => (
    <span className={`bill-badge bill-badge--${status === 'active' ? 'success' : 'neutral'}`}>
      {status === 'active' ? t('Activa') : t('Inactiva')}
    </span>
  );

  return (
    <div className="bill-page bill-page--md">
      <PageHeader
        className="mb-6"
        icon={<DollarSign className="w-6 h-6 text-fg-muted" />}
        title={t('Tarifas de Repartidores')}
        subtitle={t('Define el precio por entrega para cada repartidor')}
        actions={
          <Button onClick={handleOpenCreate} iconLeft={<Plus className="w-4 h-4" />}>
            {t('Nueva tarifa')}
          </Button>
        }
      />

      {/* Filtro */}
      <div className="bill-card bill-card--clip">
        <div className="bill-filters bill-filters--single">
          <div className="bill-field">
            <label className="bill-label">{t('Filtrar por repartidor (ID)')}</label>
            <Input
              type="text"
              value={driverFilter}
              onChange={(e) => setDriverFilter(e.target.value)}
              placeholder={t('ObjectId del repartidor...')}
              size="sm"
            />
          </div>
        </div>
      </div>

      {/* Tabla / Cards */}
      <div className="bill-card bill-card--clip">
        {loading ? (
          <TableSkeleton rows={6} columns={6} className="border-0" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<DollarSign />}
            title={t('No hay tarifas configuradas')}
            description={t('Crea una tarifa para definir el precio por entrega de un repartidor')}
          />
        ) : isMobile ? (
          <div className="bill-cards">
            {filtered.map((tariff) => {
              const driverName = getName(tariff.driverId);
              return (
              <div key={tariff._id} className="bill-card-item">
                <div className="bill-card-item__head">
                  <span className="min-w-0 flex flex-col gap-0.5">
                    <span className={`bill-card-item__title${driverName ? '' : ' bill-td-mono'}`}>
                      {driverName || tariff.driverId}
                    </span>
                    {driverName && <span className="bill-td-mono">{tariff.driverId}</span>}
                  </span>
                  {renderStatus(tariff.status)}
                </div>
                <div className="bill-card-item__rows">
                  <div className="bill-card-item__field">
                    <span className="bill-card-item__k">{t('Precio / Entrega')}</span>
                    <span className="bill-card-item__v bill-td-price">{formatCurrency(tariff.pricePerDelivery)}</span>
                  </div>
                  <div className="bill-card-item__field">
                    <span className="bill-card-item__k">{t('Precio / Intento Fallido')}</span>
                    <span className="bill-card-item__v bill-td-muted">
                      {tariff.pricePerFailedAttempt ? formatCurrency(tariff.pricePerFailedAttempt) : '-'}
                    </span>
                  </div>
                  <div className="bill-card-item__field bill-card-item__field--full">
                    <span className="bill-card-item__k">{t('Vigencia')}</span>
                    <span className="bill-card-item__v bill-td-muted">
                      {formatDate(tariff.validFrom)} → {tariff.validTo ? formatDate(tariff.validTo) : t('Sin fin')}
                    </span>
                  </div>
                </div>
                <div className="bill-card-item__foot">
                  <Button size="sm" variant="outline" onClick={() => handleOpenEdit(tariff)}>
                    {t('Editar')}
                  </Button>
                </div>
              </div>
              );
            })}
          </div>
        ) : (
          <div className="bill-grid" role="table" style={{ '--bill-cols': GRID_COLS }}>
            <div className="bill-grid__head" role="row">
              <span className="bill-grid__hcell" role="columnheader">{t('Repartidor')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Precio / Entrega')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Precio / Intento Fallido')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Vigencia')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Estado')}</span>
              <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t('Acciones')}</span>
            </div>
            {filtered.map((tariff) => {
              const driverName = getName(tariff.driverId);
              return (
              <div key={tariff._id} className="bill-grid__row" role="row">
                <span className="flex flex-col gap-0.5 min-w-0" role="cell">
                  <span className={driverName ? 'bill-td-strong' : 'bill-td-mono'}>
                    {driverName || tariff.driverId}
                  </span>
                  {driverName && <span className="bill-td-mono">{tariff.driverId}</span>}
                </span>
                <span className="bill-td-price" role="cell">{formatCurrency(tariff.pricePerDelivery)}</span>
                <span className="bill-td-muted" role="cell">
                  {tariff.pricePerFailedAttempt ? formatCurrency(tariff.pricePerFailedAttempt) : '-'}
                </span>
                <span className="bill-td-muted" role="cell">
                  {formatDate(tariff.validFrom)} →{' '}
                  {tariff.validTo ? formatDate(tariff.validTo) : <span className="bill-td-sub">{t('Sin fin')}</span>}
                </span>
                <span role="cell">{renderStatus(tariff.status)}</span>
                <span className="bill-grid__cell--actions" role="cell">
                  <Button size="sm" variant="outline" onClick={() => handleOpenEdit(tariff)}>
                    {t('Editar')}
                  </Button>
                </span>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        size="md"
        title={editId ? t('Editar tarifa') : t('Nueva tarifa de repartidor')}
        footer={
          <>
            <Button variant="outline" onClick={() => setShowModal(false)}>{t('Cancelar')}</Button>
            <Button onClick={handleSave} loading={saving}>
              {editId ? t('Guardar cambios') : t('Crear tarifa')}
            </Button>
          </>
        }
      >
        <div className="bill-field" style={{ marginBottom: '1rem' }}>
          <label className="bill-label">{t('ID del Repartidor')} <span className="text-status-error">*</span></label>
          <Input
            name="driverId"
            value={form.driverId}
            onChange={handleChange}
            placeholder={t('ObjectId del repartidor')}
            disabled={!!editId}
          />
        </div>
        <div className="bill-field" style={{ marginBottom: '1rem' }}>
          <label className="bill-label">{t('Precio por entrega (neto, sin IVA)')} <span className="text-status-error">*</span></label>
          <Input type="number" name="pricePerDelivery" value={form.pricePerDelivery} onChange={handleChange} placeholder="Ej: 3500" min={0} />
        </div>
        <div className="bill-field" style={{ marginBottom: '1rem' }}>
          <label className="bill-label">{t('Precio por intento fallido (opcional)')}</label>
          <Input type="number" name="pricePerFailedAttempt" value={form.pricePerFailedAttempt} onChange={handleChange} placeholder="Ej: 1500" min={0} />
        </div>

        {/* Override por repartidor (opcional). Gana sobre la config del operador por
            especificidad: comuna propia > comuna del operador > default propio > default operador. */}
        <div className="bill-field" style={{ marginBottom: '0.75rem' }}>
          <label className="bill-label">{t('Monto por defecto propio (opcional)')}</label>
          <Input
            type="number"
            name="defaultBaseCost"
            value={form.defaultBaseCost}
            onChange={handleChange}
            placeholder={t('Reemplaza el default del operador para este repartidor')}
            min={0}
          />
        </div>
        <div className="bill-field" style={{ marginBottom: '1rem' }}>
          <ComunaZonesEditor
            rows={form.zones}
            onChange={(zones) => setForm((f) => ({ ...f, zones }))}
            baseCost={Number(form.defaultBaseCost || form.pricePerDelivery) || 0}
            coverageAreas={coverageAreas}
            coverageLoading={coverageLoading}
          />
          {/* Tarifas por código postal propias del repartidor (D6). Pisan las del
              operador para el MISMO código; un código del operador que el repartidor no
              redefina le sigue ganando a las reglas por municipio. */}
          {postalEnabled && (
            <div style={{ marginTop: '1rem' }}>
              <PostalCodeZonesEditor
                rows={form.postalZones}
                onChange={(postalZones) => setForm((f) => ({ ...f, postalZones }))}
                baseCost={Number(form.defaultBaseCost || form.pricePerDelivery) || 0}
              />
            </div>
          )}
          <p className="bill-note" style={{ marginTop: '0.5rem' }}>
            {t('El recargo por sobredimensionado se define a nivel operador (Configuración → Pago a repartidores) y aplica a todos.')}
          </p>
        </div>

        <div className="bill-form-row">
          <div className="bill-field">
            <label className="bill-label">{t('Válido desde')} <span className="text-status-error">*</span></label>
            <Input type="date" name="validFrom" value={form.validFrom} onChange={handleChange} />
          </div>
          <div className="bill-field">
            <label className="bill-label">{t('Válido hasta (opcional)')}</label>
            <Input type="date" name="validTo" value={form.validTo} onChange={handleChange} />
          </div>
        </div>
        <div className="bill-field" style={{ margin: '1rem 0' }}>
          <label className="bill-label">{t('Estado')}</label>
          <Select name="status" value={form.status} onChange={handleChange}>
            <option value="active">{t('Activa')}</option>
            <option value="inactive">{t('Inactiva')}</option>
          </Select>
        </div>
        {!editId && (
          <div className="bill-note">
            {t('Al crear una nueva tarifa activa, las tarifas activas previas del repartidor serán desactivadas automáticamente.')}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DriverTariffs;
